import { defineStore } from 'pinia'
import { useCookie } from '@core/composable/useCookie'
import { railsApi } from '@/utils/railsApi'

function setSignedInFlag(value) {
  useCookie('clientSignedIn').value = value ? '1' : null
}

export const useClientAccountStore = defineStore('clientAccount', {
  state: () => ({
    client: null,
    changeRequests: [],
    loading: false,
    error: null,
    initialized: false,
    invitePreview: null,
  }),

  getters: {
    isSignedIn: state => !!state.client,
    pendingChangeRequest: state => state.changeRequests.find(r => r.status === 'pending') || null,
  },

  actions: {
    async bootstrap() {
      if (this.initialized)
        return this.client

      this.loading = true
      this.error = null
      try {
        if (!useCookie('clientSignedIn').value) {
          this.client = null

          return null
        }
        const data = await railsApi('/api/v1/client')

        this.client = data.client
        this.changeRequests = data.health_profile_change_requests || []
        setSignedInFlag(true)

        return this.client
      }
      catch (err) {
        if (err.status === 401) {
          this.client = null
          setSignedInFlag(false)

          return null
        }
        this.error = err.message

        return null
      }
      finally {
        this.loading = false
        this.initialized = true
      }
    },

    async login({ email, password }) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/clients/session', {
          method: 'POST',
          body: { client: { email, password } },
        })

        this.client = data.client
        this.initialized = true
        setSignedInFlag(true)

        return data.client
      }
      catch (err) {
        this.error = err.payload?.error || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async requestPasswordReset(email) {
      return railsApi('/api/v1/clients/password', {
        method: 'POST',
        body: { client: { email } },
      })
    },

    async resetPassword({ resetPasswordToken, password, passwordConfirmation }) {
      return railsApi('/api/v1/clients/password', {
        method: 'PUT',
        body: {
          client: {
            reset_password_token: resetPasswordToken,
            password,
            password_confirmation: passwordConfirmation,
          },
        },
      })
    },

    async logout() {
      try {
        await railsApi('/api/v1/clients/session', { method: 'DELETE' })
      }
      finally {
        const { clearPwaStateOnLogout } = await import('@/utils/pwaCache')

        await clearPwaStateOnLogout()
        this.client = null
        this.changeRequests = []
        this.initialized = true
        setSignedInFlag(false)
      }
    },

    async fetchInvite(token) {
      this.loading = true
      this.error = null
      try {
        this.invitePreview = await railsApi(`/api/v1/client_invitations/accept?token=${encodeURIComponent(token)}`)

        return this.invitePreview
      }
      catch (err) {
        this.invitePreview = err.payload || { valid: false, message: err.message }
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async acceptInvite(token, payload) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/client_invitations/accept', {
          method: 'POST',
          body: { token, client: payload },
        })

        this.client = data.client
        this.initialized = true
        setSignedInFlag(true)

        return data.client
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async fetchProfile() {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/client')

        this.client = data.client
        this.changeRequests = data.health_profile_change_requests || []

        return data.client
      }
      catch (err) {
        this.error = err.payload?.error || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async updateContact(payload) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/client', {
          method: 'PATCH',
          body: { client: payload },
        })

        this.client = data.client

        return data.client
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async proposeHealthChanges(payload) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/client/health_profile_change_requests', {
          method: 'POST',
          body: { health_profile: payload },
        })

        await this.fetchProfile()

        return data.health_profile_change_request
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async exportData() {
      this.loading = true
      this.error = null
      try {
        return await railsApi('/api/v1/client/data_export')
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.payload?.error || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async deleteData(confirmation) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/client/data_deletion', {
          method: 'POST',
          body: { confirmation },
        })

        const { clearPwaStateOnLogout } = await import('@/utils/pwaCache')

        await clearPwaStateOnLogout()
        this.client = null
        this.changeRequests = []
        this.initialized = true
        setSignedInFlag(false)

        return data
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.payload?.error || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },
  },
})
